import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Avatar,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Badge,
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Reply as ReplyIcon,
  CheckCircle as RespondedIcon,
  Cancel as NoResponseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { verificationAPI } from '../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const ChatManagement = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedConv, setSelectedConv] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, responded, unanswered

  useEffect(() => {
    fetchConversations();
    fetchStats();
  }, []);

  useEffect(() => {
    filterConversations();
  }, [conversations, search, filter]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await verificationAPI.getAllConversations();
      setConversations(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await verificationAPI.getConversationStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const filterConversations = () => {
    let filtered = [...conversations];

    // Filter by search
    if (search) {
      filtered = filtered.filter(conv => {
        const names = Object.values(conv.participantNames || {}).join(' ');
        return names.toLowerCase().includes(search.toLowerCase());
      });
    }

    // Filter by response status
    if (filter === 'responded') {
      filtered = filtered.filter(conv => conv.hasResponse);
    } else if (filter === 'unanswered') {
      filtered = filtered.filter(conv => conv.isUnanswered);
    }

    setFilteredConversations(filtered);
  };

  const handleViewConversation = (conv) => {
    setSelectedConv(conv);
    setDialogOpen(true);
  };

  const getStatusChip = (conv) => {
    if (conv.isUnanswered) {
      return <Chip size="small" label="No Response" color="error" icon={<NoResponseIcon />} />;
    }
    if (conv.hasResponse) {
      return <Chip size="small" label="Responded" color="success" icon={<RespondedIcon />} />;
    }
    return <Chip size="small" label="New" color="warning" />;
  };

  const getParticipantNames = (conv) => {
    return Object.values(conv.participantNames || {}).join(' ↔ ');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return moment(timestamp).fromNow();
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4">{stats.totalConversations || 0}</Typography>
            <Typography color="textSecondary">Total Conversations</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography variant="h4">{stats.conversationsWithResponse || 0}</Typography>
            <Typography color="textSecondary">With Response</Typography>
            <Typography variant="caption" color="textSecondary">
              {stats.responseRate || 0}% response rate
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
            <Typography variant="h4">{stats.unansweredConversations || 0}</Typography>
            <Typography color="textSecondary">Unanswered</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
            <Typography variant="h4">{stats.totalMessages || 0}</Typography>
            <Typography color="textSecondary">Total Messages</Typography>
            <Typography variant="caption" color="textSecondary">
              Avg: {stats.averageMessagesPerConversation || 0} per conversation
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderTopChatters = () => {
    if (!stats?.topChatters?.length) return null;

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Chatters
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="right">Messages</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.topChatters.map((chatter, index) => (
                <TableRow key={chatter.userId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{chatter.name || `User ${chatter.userId}`}</TableCell>
                  <TableCell>{chatter.phone || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={chatter.count} color="primary" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  if (loading && !conversations.length) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Chat Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchConversations(); fetchStats(); }}
        >
          Refresh
        </Button>
      </Box>

      {renderStatsCards()}
      {renderTopChatters()}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by participant name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" gap={1}>
              <Button
                variant={filter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setFilter('all')}
                size="small"
              >
                All ({conversations.length})
              </Button>
              <Button
                variant={filter === 'responded' ? 'contained' : 'outlined'}
                onClick={() => setFilter('responded')}
                size="small"
                color="success"
              >
                Responded ({conversations.filter(c => c.hasResponse).length})
              </Button>
              <Button
                variant={filter === 'unanswered' ? 'contained' : 'outlined'}
                onClick={() => setFilter('unanswered')}
                size="small"
                color="error"
              >
                Unanswered ({conversations.filter(c => c.isUnanswered).length})
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Conversation List */}
      {filteredConversations.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No conversations found
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredConversations.map((conv) => (
            <Card key={conv.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {getParticipantNames(conv)}
                      </Typography>
                      {getStatusChip(conv)}
                    </Box>
                    
                    <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
                      <Typography variant="caption" color="textSecondary">
                        Messages: {conv.totalMessages || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Senders: {conv.uniqueSenders?.length || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Last: {formatTimestamp(conv.lastUpdated)}
                      </Typography>
                      {conv.isUnanswered && (
                        <Chip 
                          size="small" 
                          label="⚠️ No reply yet" 
                          color="error" 
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {conv.lastMessage && (
                      <Box sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mb: 1 }}>
                        <Typography variant="body2">
                          <strong>{conv.lastMessage.senderName || 'User'}:</strong> {conv.lastMessage.text || 'Image message'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatTimestamp(conv.lastMessage.createdAt)}
                        </Typography>
                      </Box>
                    )}

                    {/* Sender statistics */}
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {Object.entries(conv.sentCounts || {}).map(([senderId, count]) => (
                        <Chip
                          key={senderId}
                          size="small"
                          label={`${conv.participantNames?.[senderId] || `User ${senderId}`}: ${count} messages`}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Tooltip title="View Messages">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewConversation(conv)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Expand Details">
                      <IconButton
                        size="small"
                        onClick={() => setExpandedId(expandedId === conv.id ? null : conv.id)}
                      >
                        {expandedId === conv.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={expandedId === conv.id}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Participant Details
                  </Typography>
                  <Grid container spacing={1}>
                    {conv.participantDetails?.map((p) => (
                      <Grid item xs={12} sm={6} key={p.id}>
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="body2">
                            <strong>{p.first_name || ''} {p.last_name || ''}</strong>
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            ID: {p.id} | Phone: {p.phone || 'N/A'}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary">
                            Messages: {conv.sentCounts?.[p.id] || 0}
                          </Typography>
                          {conv.initiator === p.id && (
                            <Chip size="small" label="Initiator" color="primary" />
                          )}
                          {conv.lastSender === p.id && (
                            <Chip size="small" label="Last Sender" color="info" />
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Message Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <ChatIcon />
            <Typography variant="h6">
              Conversation: {selectedConv ? getParticipantNames(selectedConv) : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedConv?.messages?.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography color="textSecondary">No messages</Typography>
            </Box>
          ) : (
            <Box>
              {selectedConv?.messages?.map((msg) => (
                <Card key={msg.id} sx={{ mb: 2, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2">
                          {msg.senderName || `User ${msg.senderId}`}
                        </Typography>
                        <Typography variant="body2">
                          {msg.text || 'Image message'}
                        </Typography>
                        {msg.imageUrl && (
                          <Box mt={1}>
                            <img
                              src={msg.imageUrl}
                              alt="Chat image"
                              style={{
                                maxWidth: '100%',
                                maxHeight: 200,
                                borderRadius: 8
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box textAlign="right">
                        <Chip
                          size="small"
                          label={msg.type || 'text'}
                          variant="outlined"
                        />
                        <Typography variant="caption" display="block" color="textSecondary">
                          {formatTimestamp(msg.createdAt)}
                        </Typography>
                        <Chip
                          size="small"
                          label={msg.status || 'sent'}
                          color={msg.status === 'sent' ? 'default' : 'success'}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatManagement;